import { useEffect, useState } from "react";
import api, { inr, formatDate, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useRealtime } from "@/lib/realtime";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";
import { Download } from "lucide-react";

export default function DistPayouts() {
  const { user, refresh } = useAuth();
  const { subscribe } = useRealtime();
  const [rows, setRows] = useState([]);
  const [amt, setAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const [avail, setAvail] = useState(null);

  const load = () => api.get("/dist/payouts").then((r) => setRows(r.data));
  // R-12: pending payout + request cap come from Account Info's numbers.
  const loadAvail = () => api.get("/dist/payout-available").then((r) => setAvail(r.data)).catch(() => {});
  useEffect(() => { load(); loadAvail(); }, []);

  useEffect(() => {
    const off = subscribe("payout.update", (evt) => {
      const { status, amount, utr } = evt.data || {};
      toast.info(`Payout ${status}`, { description: (amount ? `₹${Number(amount).toLocaleString("en-IN")}` : "") + (utr ? ` · UTR ${utr}` : "") });
      load();
      loadAvail();
    });
    return typeof off === "function" ? off : undefined;
  }, [subscribe]);

  // TDS rate mirrors the backend (Section 194H). 2% for PAN holders (default);
  // 20% only when the user is explicitly tagged as no-PAN. Kept on the client
  // as a display-only helper — the real deduction is computed server-side.
  const panMissing = (user?.pan_status || "").toLowerCase() === "missing";
  const tdsRatePct = panMissing ? 20 : 2;
  const tdsHelperText = panMissing
    ? "20% TDS (Section 194H — PAN not on file). Add PAN in Profile to reduce to 2%."
    : "2% TDS (Section 194H — commission/brokerage) will be deducted. KYC must be approved.";

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(amt);
    if (!amount || amount <= 0) { toast.error("Enter valid amount"); return; }
    if (amount > maxGross) { toast.error(`Maximum you can request is ${inr(maxGross)} (gross)`); return; }
    setBusy(true);
    try {
      await api.post("/dist/payout-request", { amount });
      toast.success("Payout requested");
      setAmt("");
      await refresh();
      await load();
      await loadAvail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const downloadPDF = async (id) => {
    try {
      const token = localStorage.getItem("samara_token");
      const res = await fetch(`${BACKEND_URL}/api/invoices/payout/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `payout-${id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Payout statement downloaded");
    } catch { toast.error("Download failed"); }
  };

  const pendingNet = Number(avail?.pending_net_rs || 0);
  const maxGross = Number(avail?.requestable_gross_rs || 0);
  const maxNet = Number(avail?.requestable_net_rs || 0);
  const withdrawnRs = Number(avail?.already_requested_gross_rs || 0);
  const sinceLabel = String(avail?.since_period_label || "").replace(/_/g, " ");
  const estTds = amt ? Math.round(parseFloat(amt || 0) * tdsRatePct) / 100 : 0;
  const estNet = amt ? Math.max(0, parseFloat(amt || 0) - estTds) : 0;

  return (
    <DashboardLayout title="Payouts" subtitle={avail ? `Pending payout (net): ${inr(pendingNet)} · same as Account Info & Wallet` : "Pending payout (net): …"}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="lg:col-span-1 bg-white border border-border p-6 rounded-sm h-fit" data-testid="payout-request-form">
          <h3 className="font-serif text-xl text-navy">Request payout</h3>
          <p className="text-xs text-navy/60 mt-1" data-testid="payout-tds-helper">{tdsHelperText}</p>
          <div className={`mt-4 text-[11px] border rounded-sm p-2.5 ${maxGross > 0 ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-slate-50 border-slate-200 text-navy/60"}`} data-testid="payout-requestable">
            {!avail ? "Loading pending payout…" : avail.payment_hold
              ? <span className="text-red-800 font-semibold" data-testid="payout-hold">Payment on hold — top up to 50 Self SP (Self SP {Number(avail.self_sp || 0)} · needs {Number(avail.self_sp_needed || 0)} more). Your earned income is kept and paid at the first weekly closing after you reach 50 Self SP.</span>
              : avail.closing_in_progress
              ? <>Closing {String(avail.closing_in_progress_period || "").replace(/_/g, " ")} is being paid — you can request again after it is marked paid.</>
              : maxGross > 0
              ? <>You can request up to <strong className="tabular-nums">{inr(maxGross)}</strong> gross (<span className="tabular-nums">{inr(maxNet)}</span> net after 2% TDS){sinceLabel ? ` · since ${sinceLabel}` : ""}.</>
              : <>Nothing pending to request{sinceLabel ? ` since ${sinceLabel}` : ""} — you are paid up.</>}
            {withdrawnRs > 0 ? (
              <div className="mt-1 text-navy/70" data-testid="payout-already-withdrawn">
                Already withdrawn since last closing: <strong className="tabular-nums">{inr(withdrawnRs)}</strong> — subtracted at the next weekly closing.
              </div>
            ) : null}
          </div>
          <label className="block text-xs uppercase tracking-widest text-navy/60 mt-5">Amount (₹)</label>
          <input data-testid="payout-amount" required value={amt} onChange={(e) => setAmt(e.target.value)} type="number" min={1} max={maxGross} className="mt-1 w-full border border-border px-3 py-3 text-sm rounded-sm focus:border-gold focus:outline-none" placeholder="0" />
          {amt && parseFloat(amt) > 0 && (
            <div className="mt-3 text-[11px] bg-slate-50 border border-slate-200 rounded-sm p-2.5 space-y-0.5" data-testid="payout-estimate">
              <div className="flex justify-between text-navy/70"><span>Gross</span><span className="tabular-nums font-semibold">{inr(parseFloat(amt))}</span></div>
              <div className="flex justify-between text-rose"><span>TDS ({tdsRatePct}%)</span><span className="tabular-nums font-semibold">− {inr(estTds)}</span></div>
              <div className="flex justify-between text-olive border-t border-slate-200 pt-1 mt-1"><span className="font-bold uppercase tracking-wide">Net</span><span className="tabular-nums font-extrabold">{inr(estNet)}</span></div>
            </div>
          )}
          <button data-testid="payout-submit" disabled={busy || user?.kyc_status !== "approved" || maxGross <= 0} className="mt-4 w-full bg-gold text-navy py-3 hover:bg-gold-deep rounded-sm font-medium disabled:opacity-50">
            {busy ? "Requesting..." : "Request payout"}
          </button>
          {user?.kyc_status !== "approved" && <p className="mt-3 text-xs text-rose">⚠ KYC must be approved first.</p>}
        </form>
        <div className="lg:col-span-2 bg-white border border-border rounded-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-border"><h3 className="font-serif text-lg text-navy">Payout history</h3></div>
          <table className="w-full text-sm">
            <thead className="bg-navy/5">
              <tr>
                {["Date", "Amount", "TDS", "Net", "UTR / Ref", "Status", ""].map((h) => <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium text-navy">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-navy/50">No payouts yet</td></tr>}
              {rows.map((p) => (
                <tr key={p.id} data-testid={`payout-${p.id}`} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 text-navy/70">{formatDate(p.created_at)}</td>
                  <td className="px-4 py-3 text-navy">{inr(p.amount)}</td>
                  <td className="px-4 py-3 text-rose">−{inr(p.tds)}</td>
                  <td className="px-4 py-3 font-semibold text-olive">{inr(p.net_amount)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-navy/70" data-testid={`dist-utr-${p.id}`}>{p.utr || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${p.status === "paid" ? "bg-olive/10 text-olive" : p.status === "rejected" ? "bg-rose/10 text-rose" : "bg-gold/10 text-gold-deep"}`}>{p.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => downloadPDF(p.id)}
                      data-testid={`dist-pay-pdf-${p.id}`}
                      title="Download PDF statement"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 border border-royal/25 text-royal hover:bg-royal/5 rounded-sm"
                    >
                      <Download size={11} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
